import type { BodyOf, RouteDef } from '@pmai/contracts';
import { useState } from 'react';
import { useApiMutation } from './api/hooks';
import { useErrorText } from './i18n';

/**
 * "Are you sure?" for one record: remembers the target, runs the route on confirm, shows the
 * API error (e.g. FR-WS-012 "related data") in the dialog and clears it when the dialog closes.
 * Spread `dialog` onto `<ConfirmDialog>`.
 */
export function useConfirm<T, R extends RouteDef>(
  route: R,
  opts: {
    invalidates?: readonly RouteDef[];
    /** Translation key of the toast after the action (see useApiMutation). */
    success?: string;
    params: (target: T) => Record<string, string>;
    body?: (target: T) => BodyOf<R>;
    onDone?: () => void;
  },
) {
  const errorText = useErrorText();
  const [target, setTarget] = useState<T | null>(null);
  const mutation = useApiMutation(route, {
    invalidates: opts.invalidates,
    success: opts.success,
    onSuccess: () => {
      setTarget(null);
      opts.onDone?.();
    },
  });
  const close = () => {
    setTarget(null);
    mutation.reset();
  };
  return {
    target,
    ask: (item: T) => {
      mutation.reset();
      setTarget(item);
    },
    dialog: {
      open: target !== null,
      onOpenChange: (open: boolean) => !open && close(),
      busy: mutation.isPending,
      error: mutation.error ? errorText(mutation.error) : null,
      onConfirm: () =>
        target !== null && mutation.mutate({ params: opts.params(target), body: opts.body?.(target) }),
    },
  };
}
